/// <amd-module name="Wit/Common/BacklogContentService/IBacklogWorkItemService" />
import { TeamContext } from "Tfs/Platform/Contracts/Core";
import { getTFSProject, ITfsPageService } from "Tfs/Platform/Page";
import { IObservableValue } from "VSS/Core/Observable";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssHistoryService } from "VSS/Platform/History";
import { IVssLocationService } from "VSS/Platform/Location";
import { MessageCardSeverity } from "VSSUI/MessageCard";
import { ITreeItem, ITreeItemEx, TreeItemProvider } from "VSSUI/Utilities/TreeItemProvider";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { BacklogLevelConfiguration, ReorderResult } from "Wit/Clients/Work/Contracts/Work";
import { getWorkClient } from "Wit/Clients/Work/RestClient/Work";
import { SerialPromiseQueue } from "Wit/ClientWorkItemService/SerialPromiseQueue";
import * as Resources from "Wit/Common/BacklogContentService/Resources";
import { BulkUpdateConstants, IWorkItemFieldChange } from "Wit/Common/BulkUpdateUtils/BulkUpdateInterfaces";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { CoreFieldRefNames, DalFields, OobFieldRefNames } from "Wit/Common/Generated/Constants";
import { ITeamSettingsService } from "Wit/Common/TeamServices/TeamSettingsService";
import { bulkUpdateWorkItems } from "Wit/Common/Utils/WorkItemUtils/CommonWorkItemActions";
import { IWorkItemFormDialogService } from "Wit/Common/WorkItemDialogs/Services/IWorkItemFormDialogService";
import { FilterManager } from "Wit/Filter/FilterManager";
import { IWorkItemTypeIconMetadataService, WorkItemTypeIconMetadataServiceId } from "Wit/UI/Services/WorkItemTypeIconMetadataService";
import { IFieldDataDictionary, IWorkItemData, IWorkItemTypeData } from "Wit/WorkItemDataManager/Contracts";
import { LinkTypeName, PageSizes } from "Wit/WorkItemModel/OM/";
import { IWorkItem } from "Wit/WorkItemModel/OM/WorkItemInterfaces";
import { IWorkItemModelService } from "Wit/WorkItemModel/Services/IWorkItemModelService";
import { IWorkItemSavingService } from "Wit/WorkItemModel/Services/WorkItemSavingService";
import { WorkItemLink } from "Wit/WorkItemModel/WorkItem/Links";
import { WorkItem } from "Wit/WorkItemModel/WorkItem/WorkItem";
import { AddWorkItemLocations, BacklogColumnSettings, CommonBacklogDataProviders, TopBottomSelection } from "./BacklogContentContants";
import { IPageWorkItemsDataProviderResponse } from "./BacklogContentDataProviderTypes";
import { DisplayState, IBacklogItem, IBacklogTree, IBacklogTreeRow, ObservableBacklogItem } from "./BacklogContentTreeTypes";
import { IBacklogWorkItemConfigurationService } from "./BacklogWorkItemConfigurationService";
import { IChangeIterationWorkItemData, ICommonBacklogContentService, ICurrentIterationData } from "./ICommonBacklogContentService";

export interface IBacklogWorkItemService extends IVssService {
    addWorkItem: (title: string, witType: string, direction: TopBottomSelection, iteration?: number) => Promise<void>;
    addCreatedWorkItem: (item: IWorkItem, parentItem: ITreeItem<IBacklogTreeRow> | undefined, direction: TopBottomSelection) => Promise<void>;
    addChildWorkItem: (item: IWorkItem, parentItem: ITreeItem<IBacklogTreeRow> | undefined, direction?: TopBottomSelection) => Promise<void>;
    changeWorkItemParentLink: (workItemIds: number[], targetWorkItemId: number, useChangeParentPerfomanceOptimization?: boolean) => Promise<void>;
    clearCache(): void;
    deleteWorkItem(items: IBacklogTreeRow[]): void;
    isBacklogWorkItemReorderableAtAreaPath(areaPath: string): boolean;
    fetchWorkItem: (itemId: number, setLoadingSpinner?: boolean) => void;
    fetchWorkItems: (workItemIds: number[]) => void;
    getFilterManager(): FilterManager;
    pageMoreItems(pageCount: number, setLoadingSpinner?: boolean): void;
    queueOperation(operation: () => Promise<void>, onFailure?: (e: any) => void, onSuccess?: () => void): void;
    reorderWorkItem(
        workItemIds: number[],
        parentItem: IBacklogTreeRow | undefined,
        prevItemId: number | null,
        nextItemId: number | null,
        currentIteration?: WorkItemClassificationNode | undefined,
        skipNextAndPrevIdsCheck?: boolean,
        useChangeParentPerfomanceOptimization?: boolean
    ): Promise<void>;
    updateBacklogTreeRowsDelete(workItems: ITreeItemEx<IBacklogTreeRow>[]): void;
    updateWorkItems(workItems: IWorkItem[], rowItems: IBacklogTreeRow[], skipUnchangedItems?: boolean): Promise<void>;
    updateWorkItemsByIdIfWereReordered(ids: number[]): Promise<void>;

    updateIteration(
        currentIterationData: ICurrentIterationData,
        item?: IBacklogTreeRow,
        successCallback?: (workItemDTO: IChangeIterationWorkItemData[], numberOfSavedWorkitems: number) => Promise<void>,
        useChangeIterationPerfomanceOptimization?: boolean,
        needCleanItemsFromTree?: boolean): Promise<void>;
}

const parentLinkType = -2;

class BacklogWorkItemService extends VssService implements IBacklogWorkItemService {
    private commonBacklogContentService: ICommonBacklogContentService;
    private debouncedFetchWorkItems: (setLoadingSpinner?: boolean) => void;
    private filterManager: FilterManager;
    // items *actively* being fetched (a request is in-flight)
    private itemsBeingFetched: number[] = [];
    // all: any item that needed to be fetched at any time in the past
    // waitList: LIFO queue for items that must still be fetched
    private itemsToFetch: { all: { [itemId: number]: number }; waitList: number[] } = { all: {}, waitList: [] };
    private timerManagement = new TimerManagement();
    private reorderedWorkItemIds: { [id: number]: boolean } = {};
    private workItemModelService: IWorkItemModelService;
    private backlogLevels: BacklogLevelConfiguration[];
    private spq = new SerialPromiseQueue();
    private newBacklogRerenderingOnFilterBehavior: boolean;

    public _serviceStart = (pageContext: IVssPageContext): void => {
        super._serviceStart(pageContext);
        this.newBacklogRerenderingOnFilterBehavior = isFeatureFlagEnabled(pageContext, FeatureFlags.LoadingSpinnerOnFilterChangeEnabled, false);
        this.commonBacklogContentService = pageContext.getService<ICommonBacklogContentService>("ICommonBacklogContentService");
        this.filterManager = new FilterManager(this.commonBacklogContentService);
        this.workItemModelService = pageContext.getService<IWorkItemModelService>("IWorkItemModelService");

        this.debouncedFetchWorkItems = this.timerManagement.debounce(this.doFetchWorkItems, 200);
    };

    public _serviceEnd = (pageContext: IVssPageContext): void => {
        super._serviceEnd(pageContext);
    };

    private async getBacklogItemsForIterationChange(item?: IBacklogTreeRow): Promise<IBacklogTreeRow[]> {
        const backlogTreeSelectedItems: IBacklogTreeRow[] = this.commonBacklogContentService.getSelectedItems(item);
        const uniqueWorkItems = new Set<IBacklogTreeRow>();

        const allowedWorkItemTypesSet = await this.commonBacklogContentService.getWorkItemTypesOfBacklogLevels([BacklogColumnSettings.Requirements, BacklogColumnSettings.Tasks]);

        // Iterate over real backlogTree (not just over filtered view)
        // Because we need to move all structure (parent with all children) to the new iteration
        for (const treeItem of backlogTreeSelectedItems) {

            // In any case - force move current selected item
            uniqueWorkItems.add(treeItem);

            // if it's not feature or epic - move all structure
            const type = treeItem?.value.workItemTypeColorAndIcon?.workItemTypeName;
            const typeIsNotExcluded = type && allowedWorkItemTypesSet.has(type);
            if (typeIsNotExcluded) {
                const flattenedTreeRow = await treeItem.getFlattenedArray(async (child: IBacklogTreeRow): Promise<boolean> => {
                    // In case of workItem is in Completed state - validation will fail
                    // We don't need to move it - invert completedState
                    return !(await this.commonBacklogContentService.checkCompletedStateAsync(child));
                }, false);
                flattenedTreeRow.forEach(el => uniqueWorkItems.add(el));
            }
        }

        const workItems = Array.from(uniqueWorkItems);
        return workItems;
    }

    public async updateIteration(
        currentIterationData: ICurrentIterationData,
        currentRow?: IBacklogTreeRow,
        successCallback?: (workItemDTO: IChangeIterationWorkItemData[], numberOfSavedWorkitems: number) => Promise<void>,
        useChangeIterationPerfomanceOptimization?: boolean,
        needCleanItemsFromTree?: boolean
    ): Promise<void> {
        const backlogTreeRows = await this.getBacklogItemsForIterationChange(currentRow);

        const workItemDTO: IChangeIterationWorkItemData[] = backlogTreeRows.map(backlogTreeRow => {
            return {
                workItemId: backlogTreeRow.id ?? (backlogTreeRow.value.coreColumnMap[CoreFieldRefNames.Id] as number),
                workItemTypeName: backlogTreeRow.value.workItemTypeColorAndIcon?.workItemTypeName || "",
                workItemIterationPath: backlogTreeRow.value.coreColumnMap[CoreFieldRefNames.IterationPath] as string,
                workItemIterationId: backlogTreeRow.value.coreColumnMap[CoreFieldRefNames.IterationId] as number,
                workItemProjectName: backlogTreeRow.value.coreColumnMap[CoreFieldRefNames.TeamProject] as string,
            };
        });

        const updateResult = await this.commonBacklogContentService.updateIterationCommonLogic(
            currentIterationData,
            workItemDTO,
            this.filterManager,
            this.updateWorkItemsByIdIfWereReordered,
            useChangeIterationPerfomanceOptimization,
            needCleanItemsFromTree
        );

        if (updateResult.changeIterationIsSuccessful) {
            await successCallback?.(workItemDTO, updateResult.numberOfSavedWorkitems);
        }
    }

    public addWorkItem = async (title: string, witType: string, direction: TopBottomSelection, iteration?: number): Promise<void> => {
        const project = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData()?.project;
        const teamSettingsService = this.pageContext.getService<ITeamSettingsService>("ITeamSettingsService");
        const teamSettings = teamSettingsService.getTeamSettingsObservable().value;
        const newWorkItemFields: IFieldDataDictionary = {};

        // Create a new work item object on the front-end
        const newWorkItem: IWorkItemData = {
            projectId: project?.id ?? "",
            fields: newWorkItemFields,
            revisions: null,
            tags: [],
            files: [],
            referencedPersons: {},
            tempId: WorkItem.getTempId(),
            loadTime: new Date(),
            currentExtensions: [],
            referencedNodes: { areaNodes: [], iterationNodes: [] }
        };

        const types: IWorkItemTypeData[] | null = await this.workItemModelService.getWorkItemTypeData(project?.id ?? "", [witType]);

        if (types && types.length === 1) {
            const workItem: IWorkItem = await this.workItemModelService.createWorkItem(types[0], project?.id ?? "", newWorkItem);

            workItem.setFieldValueByName(CoreFieldRefNames.Title, title);
            workItem.setFieldValueByName(CoreFieldRefNames.WorkItemType, witType);
            workItem.setFieldValueByName(CoreFieldRefNames.TeamProject, project?.name ?? "");
            workItem.setFieldValueByName(CoreFieldRefNames.Rev, 1);

            if (teamSettings?.teamFieldName === CoreFieldRefNames.AreaPath) {
                workItem.setFieldValueByName(CoreFieldRefNames.AreaPath, teamSettings.teamFieldDefaultValue);
            }

            if (iteration) {
                workItem.setFieldValueByName(CoreFieldRefNames.IterationId, iteration);
            }

            await this.addCreatedWorkItem(workItem, undefined, direction);
        }
    };

    public queueOperation(operation: () => Promise<void>, onFailure?: (e: any) => void, onSuccess?: () => void): void {
        this.spq.queue(async () => {
            try {
                await operation();
                onSuccess?.();
            } catch (e: unknown) {
                onFailure?.(e);
            }
        });
    }

    private async finalizeItem({
        workItem,
        item,
        backlogTree,
        parentItem,
        direction,
        columnMapDict,
        placeholderTreeItem,
        witType
    }: {
        workItem: IWorkItem;
        item: ITreeItem<IBacklogTreeRow> | undefined;
        backlogTree: IObservableValue<IBacklogTree | undefined>;
        parentItem: ITreeItem<IBacklogTreeRow> | undefined;
        direction: TopBottomSelection;
        columnMapDict: {
            [columnId: string]: number;
        };
        placeholderTreeItem: ITreeItem<IBacklogTreeRow>;
        witType: string;
    }) {
        // Add to bottom by default. Otherwise if top or selection, re-order work item to correct location and send reorder request to backend
        const rows: ITreeItem<IBacklogTreeRow>[] | undefined = backlogTree.value?.rows;
        if (rows) {
            const reorderResult = await this.reorderTreeWorkItems(rows, direction, workItem.id, parentItem, backlogTree.value, item);

            // Update local newTreeItem with backend values
            const updatedCoreColumns: { [columnId: string]: string | number | null } = {};

            for (const key in columnMapDict) {
                if (key === CoreFieldRefNames.Id) {
                    updatedCoreColumns[key] = workItem.id;
                } else if (key === OobFieldRefNames.StackRank) {
                    updatedCoreColumns[key] = reorderResult && reorderResult[0] && reorderResult[0].order ? reorderResult[0].order : null;
                } else if (key === CoreFieldRefNames.Rev) {
                    updatedCoreColumns[key] = workItem.revision.value;
                } else {
                    updatedCoreColumns[key] = workItem.getFieldValueByName(key);
                }
            }

            const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
            const workItemUrl = locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
                id: workItem.id.toString(),
                project: getTFSProject(this.pageContext)?.name ?? ""
            });

            const treeItem: ITreeItem<ObservableBacklogItem> = await this.commonBacklogContentService.createNewTreeItem(
                workItem.id,
                columnMapDict,
                updatedCoreColumns,
                witType,
                workItemUrl,
                placeholderTreeItem.data.value.order
            );

            backlogTree.value?.itemProvider.splice(
                parentItem,
                [placeholderTreeItem],
                [
                    {
                        items: [treeItem],
                        insertAfter: placeholderTreeItem
                    }
                ]
            );

            if (parentItem?.childItems) {
                parentItem.data.children = parentItem.childItems.map(item => item.data);
            }
            this.updateBacklogTreeRowsAdd(treeItem, parentItem);

            if (this.commonBacklogContentService.getDisplayState().value === DisplayState.ZeroData) {
                this.commonBacklogContentService.setDisplayState(DisplayState.HasData);
            }

            this.updateFilter();

            // reset order of columns only after setting of actual id to workItem (treeNode)
            this.commonBacklogContentService.resetOrderColumn();
        }
    }

    private async getInsertAfterItemAlignedWithDirection(
        direction?: TopBottomSelection
    ): Promise<{ insertAfterItem: ITreeItem<IBacklogTreeRow> | undefined; parentItem: ITreeItem<IBacklogTreeRow> | undefined }> {
        const historyService = this.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const backlogTree = this.commonBacklogContentService.getBacklogTree();
        const backlogLevels = await this.getBacklogLevels();
        const showParents = historyService.getState().showParents === "true";
        const insertIndex = this.getInsertIndex(direction, backlogTree, showParents) ?? 0;
        const selectedItem = backlogTree.value?.itemProvider.value[insertIndex + 1 ?? 0];

        let item: ITreeItem<IBacklogTreeRow> | undefined = undefined;
        let parentItem: ITreeItem<IBacklogTreeRow> | undefined = undefined;
        const treeItem = insertIndex >= 0 ? backlogTree.value?.itemProvider.value[insertIndex] : undefined;
        item = treeItem?.underlyingItem;

        if (direction === AddWorkItemLocations.Selection && selectedItem) {
            const selectedLevel = this.getWorkItemBacklogLevel(selectedItem, backlogLevels);
            const backlogLevel = this.getCurrentBacklogLevel(backlogLevels);

            if (!selectedLevel || !backlogLevel) {
                // works for case when we selected 'Unparanted tasks' folder in Sprints hub.
                parentItem = undefined;
                item = selectedItem.underlyingItem;
            } else if (selectedLevel.rank <= backlogLevel.rank) {
                if (!selectedItem.underlyingItem.data.value.backlogLevel) {
                    const newItemToInsertAfter = this.getFirstAppropriateParentItem(backlogLevels, backlogLevel, selectedItem);
                    parentItem = newItemToInsertAfter?.parentItem?.underlyingItem;
                    item = newItemToInsertAfter?.underlyingItem;
                } else {
                    parentItem = selectedItem.underlyingItem;
                    item = undefined;
                }
            } else if (selectedLevel.rank > backlogLevel.rank) {
                const firstAppropriateItem = this.findAppropriateChild(backlogTree.value?.rows, backlogLevels, backlogLevel, selectedItem);
                if (firstAppropriateItem) {
                    parentItem = firstAppropriateItem;
                    item = undefined;
                }
            }
        } else if (showParents) {
            parentItem = this.commonBacklogContentService.getOrAddUnparentedFolder();

            if (direction === AddWorkItemLocations.Top) {
                item = undefined;
            }
        }

        if (direction === AddWorkItemLocations.Bottom) {
            if (showParents && parentItem) {
                item = parentItem.childItems?.[parentItem.childItems.length - 1];
            } else {
                item = backlogTree.value?.rows[backlogTree.value.rows.length - 1];
            }
        }

        if (direction === AddWorkItemLocations.Top && item && item.data.id > 0) {
            // remove item in case of first item is not a folder (e.g. Unparanted tasks in sprints hub)
            item = undefined;
        }

        if (parentItem) {
            backlogTree.value?.itemProvider.expand(parentItem, true);
        }

        return { insertAfterItem: item, parentItem };
    }

    public addCreatedWorkItem = async (
        workItem: IWorkItem,
        parentItem: ITreeItem<IBacklogTreeRow> | undefined,
        direction: TopBottomSelection
    ): Promise<void> => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();

        if (!backlogTree.value) {
            return;
        }

        const columnMapDict = backlogTree.value.columnMap;
        const coreColumns: { [columnId: string]: string | number | null } = {};

        for (const key in columnMapDict) {
            coreColumns[key] = workItem.getFieldValueByName(key);
        }

        const insertAfterItemResult = await this.getInsertAfterItemAlignedWithDirection(direction);
        const insertAfterItem = insertAfterItemResult.insertAfterItem;
        parentItem = parentItem ?? insertAfterItemResult.parentItem;

        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        const workItemUrl = locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
            id: workItem.id.toString(),
            project: getTFSProject(this.pageContext)?.name ?? ""
        });

        const order = insertAfterItem ? insertAfterItem.data.value.order : undefined;
        const treeItem: ITreeItem<ObservableBacklogItem> = await this.commonBacklogContentService.createNewTreeItem(
            WorkItem.getTempId(),
            columnMapDict,
            coreColumns,
            workItem.getTypeData().name,
            workItemUrl,
            order,
            parentItem?.data
        );

        backlogTree.value.itemProvider.add(treeItem, parentItem, insertAfterItem);

        if (parentItem && parentItem.data.id >= 0) {
            workItem.addLinks([await WorkItemLink.create(this.pageContext, LinkTypeName.Parent_Link_Name, parentItem.data.id)]);
        }

        const finalizeItemDelegate = async () => {
            await this.finalizeItem({
                workItem: workItem,
                item: insertAfterItem,
                backlogTree,
                parentItem,
                direction,
                columnMapDict,
                placeholderTreeItem: treeItem,
                witType: workItem.getTypeData().name
            });
        };

        try {
            if (await workItem.isValid()) {
                const workItemSavingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
                await workItemSavingService.saveWorkItemsBatchAsync([workItem]);
                await finalizeItemDelegate();
            } else {
                // if the item isn't valid (usually because of user-defined rules), we show a dialog and let the user fix it
                const dialogService = this.pageContext.getService<IWorkItemFormDialogService>("IWorkItemFormDialogService");
                dialogService.showWorkItemFormDialog(workItem, finalizeItemDelegate, {
                    onDismiss: () => {
                        if (workItem.isNew()) {
                            // if the work item form was dismissed without successfully saving the work item, remove it
                            backlogTree.value?.itemProvider.remove(treeItem);
                        }
                    }
                });
            }
        } catch (error) {
            backlogTree.value.itemProvider.remove(treeItem);
            this.commonBacklogContentService.showErrorMessage(error);
        }
    };

    public addChildWorkItem = async (
        item: IWorkItem,
        parentItem: ITreeItem<IBacklogTreeRow> | undefined,
        direction?: TopBottomSelection
    ): Promise<void> => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();

        if (!backlogTree.value) {
            return;
        }

        // Add placeholder work item as placeholder before backend call
        const columnMapDict = backlogTree.value.columnMap;

        const coreColumns: { [columnId: string]: string | number | null } = {};

        for (const key in columnMapDict) {
            coreColumns[key] = item.getFieldValueByName(key);
        }

        let insertIndex = this.getInsertIndex(direction, backlogTree, false);

        const insertAfterItem = parentItem?.childItems
            ? parentItem?.childItems?.[parentItem.childItems.length - 1]
            : insertIndex !== undefined && insertIndex >= 0
                ? backlogTree.value.itemProvider.value[insertIndex].underlyingItem
                : undefined;

        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        const workItemUrl = locationService.routeUrl("ms.vss-work-web.work-items-form-route", {
            id: item.id.toString(),
            project: getTFSProject(this.pageContext)?.name ?? ""
        });

        const order = insertAfterItem ? insertAfterItem.data.value.order : undefined;
        const treeItem: ITreeItem<ObservableBacklogItem> = await this.commonBacklogContentService.createNewTreeItem(
            item.id,
            columnMapDict,
            coreColumns,
            item.getTypeData().name,
            workItemUrl,
            order,
            parentItem?.data
        );

        backlogTree.value.itemProvider.add(treeItem, parentItem, insertAfterItem);
        parentItem?.data.children.push(treeItem.data);
        this.updateBacklogTreeRowsAdd(treeItem, parentItem);
        this.updateFilter();
        this.commonBacklogContentService.resetOrderColumn();
    };

    public changeWorkItemParentLink = async (workItemIds: number[], targetWorkItemId: number, useChangeParentPerfomanceOptimization?: boolean): Promise<void> => {
        const linkTypeName = "System.LinkTypes.Hierarchy-Reverse";
        const workItems: IWorkItem[] = await this.getWorkItemFromModelService(workItemIds);

        await Promise.all(
            workItems.map(async workItem => {
                const existingParentLink = workItem.getLinks().find(link => link.linkData.LinkType === parentLinkType);
                if (existingParentLink) {
                    workItem.removeLinks([existingParentLink]);
                }

                //avoid to add link to unparented element
                if (targetWorkItemId > 0) {
                    workItem.addLinks([await WorkItemLink.create(this.pageContext, linkTypeName, targetWorkItemId)]);
                }
            })
        );

        if (useChangeParentPerfomanceOptimization) {
            const changesMap = new Map<string, IWorkItemFieldChange[]>();
            const changesList: IWorkItemFieldChange[] = [];

            changesList.push({ fieldName: CoreFieldRefNames.Parent, value: targetWorkItemId });
            changesMap.set(BulkUpdateConstants.AllTypes, changesList);
            try {
                this.commonBacklogContentService.setBulkBacklogOperationIsInProgress(true);
                await bulkUpdateWorkItems(
                    this.pageContext,
                    workItems,
                    changesMap,
                    {
                        selection: this.commonBacklogContentService.getTreeSelection(),
                        useProcessDialog: true,
                        onModifyItems: async workItems => {
                            const batchSaveOperationIsSuccessful = await this.saveWorkItems(
                                workItems,
                                useChangeParentPerfomanceOptimization);
                            if (!batchSaveOperationIsSuccessful) {
                                return;
                            }

                            await this.commonBacklogContentService.updateTreeLocally(workItems, (itemValue: IBacklogItem) => {
                                itemValue.coreColumnMap[CoreFieldRefNames.Parent] = targetWorkItemId;
                            });
                        },
                    });
                return;
            } finally {
                this.commonBacklogContentService.setBulkBacklogOperationIsInProgress(false);
            }
        }

        await this.commonBacklogContentService.updateTreeLocally(workItems, (itemValue: IBacklogItem) => {
            itemValue.coreColumnMap[CoreFieldRefNames.Parent] = targetWorkItemId;
        });

        await this.saveWorkItems(workItems);
    };

    public clearCache = () => {
        this.itemsToFetch = { all: {}, waitList: [] };
    };

    public deleteWorkItem = (items: IBacklogTreeRow[]): void => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();
        // already deleted in backend if this is called, just need to update data provider
        if (backlogTree.value) {
            const data = backlogTree.value;
            const deletedMap = items.reduce((dict: { [id: number]: IBacklogTreeRow }, item: IBacklogTreeRow) => {
                dict[item.value.coreColumnMap[CoreFieldRefNames.Id] as number] = item;
                return dict;
            }, {});

            const getId = (item: ITreeItem<IBacklogTreeRow>) => {
                return item.data.value.coreColumnMap[CoreFieldRefNames.Id] as number;
            };

            const filterDeleted = (items: ITreeItem<IBacklogTreeRow>[]) => {
                return items.filter(item => {
                    if (item.data.parent && deletedMap[item.data.parent.value.coreColumnMap[CoreFieldRefNames.Id] as number]) {
                        // if parent was deleted, then remove this item as well
                        deletedMap[getId(item)] = item.data;
                    }

                    return !deletedMap[getId(item)];
                });
            };

            const rows = filterDeleted(data.rows);

            let allRows = data.allRows;
            for (const key in deletedMap) {
                delete allRows[key];
            }

            const filterDeletedRecursive = (items: ITreeItem<IBacklogTreeRow>[] | undefined) => {
                if (!items) {
                    return undefined;
                }

                const filteredItems = filterDeleted(items);
                filteredItems.forEach(item => {
                    item.childItems = filterDeletedRecursive(item.childItems);
                });

                return filteredItems;
            };

            const treeProviderItems = filterDeletedRecursive(
                data.itemProvider.value.filter(item => item.depth === 0).map(item => item.underlyingItem)
            );
            backlogTree.value = {
                ...backlogTree.value,
                allRows,
                rows,
                itemProvider: new TreeItemProvider(treeProviderItems)
            };

            if (backlogTree.value.itemProvider.length === 0) {
                this.commonBacklogContentService.setDisplayState(DisplayState.ZeroData);
            }

            this.updateBacklogTreeRowsDelete(data.itemProvider.value.filter(item => deletedMap[item.underlyingItem.data.id]));
        }

        this.updateFilter();
    };

    public fetchWorkItem = (itemId: number, setLoadingSpinner?: boolean): void => {
        // if item is not found, track it and then push it to the front
        // that way the most recent items will get fetched
        if (itemId >= 0 && !this.itemsToFetch.all[itemId]) {
            this.itemsToFetch.all[itemId] = itemId;
            this.itemsToFetch.waitList.unshift(itemId);

            if (setLoadingSpinner && this.commonBacklogContentService.getDisplayState().value !== DisplayState.Loading) {
                this.commonBacklogContentService.setDisplayState(DisplayState.Loading);
            }

            // this will fetch all items in itemsToFetch after 200ms pass without any calls to fetchWorkItem
            this.debouncedFetchWorkItems(setLoadingSpinner);
        }
    };

    public getFilterManager = () => {
        return this.filterManager;
    };

    public pageMoreItems = (toPageCount: number, setLoadingSpinner?: boolean) => {
        const workItemIdsSet = this.commonBacklogContentService.getWorkItemIdsSet();
        const allRows = this.commonBacklogContentService.getBacklogTree().value?.allRows;
        if (!allRows) {
            return;
        }

        while (toPageCount > 0 && workItemIdsSet.size > 0) {
            const id = workItemIdsSet.values().next().value;
            if (!id) {
                break;
            }
            const backlogWorkItemService = this.pageContext.getService<IBacklogWorkItemService>("IBacklogWorkItemService");
            backlogWorkItemService.fetchWorkItem(id, setLoadingSpinner);
            workItemIdsSet.delete(id);
            toPageCount--;
        }
    };

    public reorderWorkItem = async (
        workItemIds: number[],
        parentItem: IBacklogTreeRow | undefined,
        prevItemId: number | null,
        nextItemId: number | null,
        currentIteration?: WorkItemClassificationNode | undefined,
        skipNextAndPrevIdsCheck?: boolean,
        useChangeParentPerfomanceOptimization?: boolean
    ): Promise<void> => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();
        const itemProvider = backlogTree.value?.itemProvider.value;

        if (itemProvider) {
            // Determine if the moved work item's changed parent
            let parentChanged = false;
            let iterationPath = null;
            const workItems: IWorkItem[] = await this.getWorkItemFromModelService(workItemIds);
            await Promise.all(
                workItems.map(async workItem => {
                    iterationPath = workItem.getFieldValueByName(CoreFieldRefNames.IterationPath);
                    const existingParentLink = workItem.getLinks().find(link => link.linkData.LinkType === parentLinkType);
                    if (existingParentLink) {
                        if (existingParentLink.linkData.ID != parentItem?.id) {
                            parentChanged = true;
                        }
                    }
                    // case then we set parent to unparented Item
                    else if (parentItem?.id) {
                        parentChanged = true;
                    }
                })
            );

            if (parentItem && parentChanged) {
                await this.changeWorkItemParentLink(workItemIds, parentItem.id, useChangeParentPerfomanceOptimization);
            }

            // reorder only reorderable backlog work items
            const reorderableWorkItems = workItemIds.filter(id => {
                const workItemToCheck = workItems.find(workItem => workItem.id === id);
                if (!workItemToCheck) {
                    return false;
                }
                const areaPath = workItemToCheck.getFieldValueByName(CoreFieldRefNames.AreaPath);
                return this.isBacklogWorkItemReorderableAtAreaPath(areaPath);
            });

            if (reorderableWorkItems.length) {
                // We check prevItemId and nextItemId items for being reorderable items, and in case they are not - we set them to null.
                // That is needed to avoid backend errors, same way as it is implemented in OWP.
                if (!skipNextAndPrevIdsCheck) {
                    ({ prevItemId, nextItemId } = this.processNextAndPreviousItemIds(itemProvider, prevItemId, nextItemId, workItems));
                }
                const reorderResult = await this.reorderBacklogWorkItems(
                    reorderableWorkItems,
                    prevItemId,
                    nextItemId,
                    parentItem,
                    iterationPath,
                    currentIteration?.identifier
                );
                this.updateWorkItemsRevision(reorderResult, workItems);
            }

            this.setReorderedWorkItemIds(workItemIds, true);
        }
    };

    private processNextAndPreviousItemIds(
        itemProvider: ITreeItemEx<IBacklogTreeRow>[],
        prevItemId: number | null,
        nextItemId: number | null,
        workItems: IWorkItem[]
    ): { prevItemId: number | null; nextItemId: number | null } {
        if (!workItems.length) {
            return { prevItemId, nextItemId };
        }

        let reorderBacklogLevel: number | undefined;
        let prevItemBacklogLevel: number | undefined;
        let nextItemBacklogLevel: number | undefined;
        let prevItem: ITreeItemEx<IBacklogTreeRow> | undefined;
        let nextItem: ITreeItemEx<IBacklogTreeRow> | undefined;
        for (const item of itemProvider) {
            const itemId = item.underlyingItem.data.id;
            if (itemId === workItems[0].id) {
                reorderBacklogLevel = item.depth;
            }

            if (itemId === prevItemId) {
                prevItemBacklogLevel = item.depth;
                prevItem = item;
            }

            if (itemId === nextItemId) {
                nextItemBacklogLevel = item.depth;
                nextItem = item;
            }

            if (reorderBacklogLevel && prevItemBacklogLevel && nextItemBacklogLevel) {
                break;
            }
        }

        if (reorderBacklogLevel !== prevItemBacklogLevel || !this.isBacklogWorkItemReorderable(prevItem)) {
            prevItemId = null;
        }

        if (reorderBacklogLevel !== nextItemBacklogLevel || !this.isBacklogWorkItemReorderable(nextItem)) {
            nextItemId = null;
        }

        return { prevItemId, nextItemId };
    }

    public isBacklogWorkItemReorderable(item: ITreeItemEx<IBacklogTreeRow> | undefined): boolean {
        if (!item) {
            return true;
        }

        const areaPath = item.underlyingItem.data.value.coreColumnMap[CoreFieldRefNames.AreaPath]?.toString();
        if (areaPath && !this.isBacklogWorkItemReorderableAtAreaPath(areaPath)) {
            return false;
        }

        return true;
    }

    public updateBacklogTreeRowsDelete = (workItems: ITreeItemEx<IBacklogTreeRow>[]) => {
        const useChangeIterationPerfomanceOptimization = isFeatureFlagEnabled(this.pageContext, FeatureFlags.UseChangeIterationPerfomanceOptimization, false);
        const backlogTree = this.commonBacklogContentService.getBacklogTree().value;
        if (!backlogTree) {
            return;
        }
        const allIdsMap = this.commonBacklogContentService.getAllIdsToRowsMap();
        const allRows = backlogTree.allRows!;
        const rows = backlogTree.rows!;
        for (const workItem of workItems) {
            backlogTree.itemProvider.splice(workItem.parentItem?.underlyingItem, [workItem.underlyingItem]);
            allIdsMap.delete(workItem.underlyingItem.data.id);
            delete allRows[workItem.underlyingItem.data.id];

            //we need to find index of the row that has the same id as the workItem we are deleting
            const index = rows.findIndex(row => row.data?.id === workItem.underlyingItem?.data?.id);
            if ((useChangeIterationPerfomanceOptimization) && index >= 0) {
                rows.splice(index, 1);
            }

            if (workItem.parentItem) {
                // delete item from parent children
                const parent = backlogTree.itemProvider.value.find(
                    item => item.underlyingItem.data.id === workItem.parentItem!.underlyingItem.data.id
                );
                if (parent) {
                    parent.underlyingItem.data.children = parent.underlyingItem.data.children.filter(
                        item => item.id !== workItem.underlyingItem.data.id
                    );
                    backlogTree.allRows[workItem.parentItem.underlyingItem.data.id] = parent.underlyingItem.data;
                    allIdsMap.set(workItem.parentItem.underlyingItem.data.id, parent.underlyingItem.data);
                }
            }
        }
    };

    public updateWorkItems = async (workItems: IWorkItem[], rowItems: IBacklogTreeRow[], skipUnchangedItems?: boolean) => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();
        const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        const fieldUpdates = workItems.map(item => ({ workItem: item, updates: item.getFieldUpdates() }));

        try {
            await savingService.saveWorkItemsBatchAsync(workItems);
            this.commonBacklogContentService.clearErrorMessage();
        } catch (error) {
            this.updateIsValid(
                workItems.map(wi => ({ workItem: wi, isValid: false })),
                skipUnchangedItems
            );
            this.commonBacklogContentService.showBulkSaveErrorMessage(error);
            return;
        }

        this.updateIsValid(
            workItems.map(wi => ({ workItem: wi, isValid: true })),
            skipUnchangedItems
        );

        if (backlogTree.value) {
            const data = backlogTree.value;
            const updatedMap = rowItems.reduce((dict: { [id: number]: IBacklogTreeRow }, item: IBacklogTreeRow) => {
                dict[item.value.coreColumnMap[CoreFieldRefNames.Id] as number] = item;
                return dict;
            }, {});

            fieldUpdates.forEach(async fieldUpdate => {
                const { workItem, updates } = fieldUpdate;
                const rowItem = updatedMap[workItem.id];

                if (rowItem) {
                    Object.keys(updates).forEach(fieldId => {
                        const fieldName = workItem.getFieldById(parseInt(fieldId))?.fieldDefinition.referenceName;
                        if (fieldName) {
                            rowItem.value.coreColumnMap[fieldName] = updates[parseInt(fieldId)];
                        }
                    });

                    this.updateRowItemColumnValues(rowItem, data);

                    if (updates[DalFields.WorkItemTypeId]) {
                        const projectId = getTFSProject(this.pageContext)?.id ?? "";
                        const iconService = this.pageContext.getService<IWorkItemTypeIconMetadataService>(WorkItemTypeIconMetadataServiceId);
                        const newIcon = await iconService.getWorkItemIcon(projectId, updates[DalFields.WorkItemTypeId]);
                        rowItem.value = { ...rowItem.value, workItemTypeColorAndIcon: newIcon };
                        this.commonBacklogContentService.showErrorMessage(Resources.ChangesRequireRefresh, MessageCardSeverity.Warning);
                    }
                }
            });

            const treeItems = data.itemProvider.value.filter(item => item.depth === 0).map(item => item.underlyingItem);
            backlogTree.value = { ...backlogTree.value, itemProvider: new TreeItemProvider(treeItems) };

            this.updateFilter();
        }
    };

    private updateRowItemColumnValues = (rowItem: IBacklogTreeRow, backlogTree: IBacklogTree): void => {
        const invertedColumnMap: {
            [columnIndex: number]: string;
        } = {};

        for (const columnId in backlogTree.columnMap) {
            if (backlogTree.columnMap.hasOwnProperty(columnId)) {
                const columnIndex = backlogTree.columnMap[columnId];
                invertedColumnMap[columnIndex] = columnId;
            }
        }

        rowItem.value.columnValues = rowItem.value.columnValues.map((value, index) => rowItem.value.coreColumnMap[invertedColumnMap[index]]);
    };

    public isBacklogWorkItemReorderableAtAreaPath(areaPath: string): boolean {
        return this.commonBacklogContentService.teamOwnsWorkItem(areaPath);
    }

    private async getBacklogLevels(): Promise<BacklogLevelConfiguration[]> {
        if (!this.backlogLevels) {
            this.backlogLevels = await this.commonBacklogContentService.getAllBacklogLevels();
        }
        return this.backlogLevels;
    }

    public fetchWorkItems = async (workItemIds: number[]): Promise<void> => {
        const retrievedItems = await this.fetchWorkItemsCore(workItemIds);

        if (retrievedItems) {
            await this.processFetchedWorkItems(retrievedItems)
            if (this.newBacklogRerenderingOnFilterBehavior) {
                this.commonBacklogContentService.resetOrderColumn();
                this.updateFilter();
            }
        }
    };

    private doFetchWorkItems = async (showLoadingSpinner?: boolean): Promise<void> => {
        // if we're already fetching items, do nothing
        if (this.itemsBeingFetched.length > 0) {
            return;
        }

        const displayState = this.commonBacklogContentService.getDisplayState();

        if (showLoadingSpinner && displayState.value !== DisplayState.Loading) {
            this.commonBacklogContentService.setDisplayState(DisplayState.Loading);
        }

        // take up to the first PageSizes.QUERY missing items, if there are more we will schedule another fetch after this returns
        this.itemsBeingFetched = this.itemsToFetch.waitList.splice(0, PageSizes.QUERY);

        const retrievedItems = await this.fetchWorkItemsCore(this.itemsBeingFetched);

        if (retrievedItems) {
            await this.processFetchedWorkItems(retrievedItems);
        }

        this.itemsBeingFetched = [];

        if (retrievedItems && this.itemsToFetch.waitList.length > 0) {
            this.debouncedFetchWorkItems(showLoadingSpinner);
        } else {
            if (this.newBacklogRerenderingOnFilterBehavior) {
                this.commonBacklogContentService.resetOrderColumn(true /*suspend notifications*/);
                this.updateFilter();
                this.commonBacklogContentService.getIsLoadingNextBatchObservable().value = false;
            }

            if (showLoadingSpinner && displayState.value === DisplayState.Loading) {
                this.commonBacklogContentService.updateDisplayState();
            }
        }
    };

    private fetchWorkItemsCore = async (workItemIds: number[]): Promise<IPageWorkItemsDataProviderResponse | undefined> => {
        const backlogTree = this.commonBacklogContentService.getBacklogTree().value;

        // if we're already fetching items or if the backlogTree doesn't exist, do nothing
        if (workItemIds.length == 0 || !backlogTree) {
            return;
        }

        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        const response = await contributionService.getDataAsync<IPageWorkItemsDataProviderResponse>(
            CommonBacklogDataProviders.PageWorkItemsDataProvider,
            {
                fields: Object.keys(backlogTree.columnMap).join(","),
                workItemIds: workItemIds.join(",")
            },
            true
        );

        return response;
    };

    private processFetchedWorkItems = async (response: IPageWorkItemsDataProviderResponse): Promise<void> => {
        const workItemConfigurationService = this.pageContext.getService<IBacklogWorkItemConfigurationService>(
            "IBacklogWorkItemConfigurationService"
        );

        const workItemStateAndColorDictPromise = workItemConfigurationService.getWorkItemStateAndColorDictionary();
        const workItemTypeColorAndIconsPromise = workItemConfigurationService.getWorkItemTypeColorAndIcons();

        const [workItemStateAndColorDict, workItemTypeColorAndIcons] = await Promise.all([
            workItemStateAndColorDictPromise,
            workItemTypeColorAndIconsPromise
        ]);

        await this.commonBacklogContentService.processFetchedWorkItems(response, workItemStateAndColorDict, workItemTypeColorAndIcons);
        if (!this.newBacklogRerenderingOnFilterBehavior) {
            this.updateFilter();
        }
    };

    private getInsertIndex = (
        direction: TopBottomSelection | undefined,
        backlogTree: IObservableValue<IBacklogTree | undefined>,
        showParents: boolean
    ) => {
        if (direction === AddWorkItemLocations.Bottom || (showParents && direction === AddWorkItemLocations.Top)) {
            const length: number | undefined = backlogTree.value?.itemProvider?.value?.length;
            return length ? length - 1 : undefined;
        } else if (direction === AddWorkItemLocations.Selection) {
            const selectedRow = this.commonBacklogContentService.getTreeSelection();
            // reduce index by 1 so we add the item to the position selected instead of *after* the selection
            return selectedRow?.value[0]?.beginIndex - 1;
        }

        return undefined;
    };

    private getWorkItemFromModelService = async (workItemIds: number[], isCacheDisabled?: boolean): Promise<IWorkItem[]> => {
        return this.workItemModelService.getWorkItems(workItemIds, undefined, undefined, undefined, isCacheDisabled);
    };

    private updateWorkItemsRevision(result: ReorderResult[], workItems: IWorkItem[]): void {
        if (!result || result.length === 0 || !workItems || workItems.length === 0) {
            return;
        }

        const reorderedWorkItems = workItems.filter(wi => result.some(res => res.id === wi.id));

        reorderedWorkItems.forEach(workitem => {
            workitem.revision.value++;
        });
    }

    private async reorderBacklogWorkItems(
        workItemIds: number[],
        previousItemId: number | null,
        nextItemId: number | null,
        parentItem?: IBacklogTreeRow,
        iterationPath?: string | null,
        iterationId?: string
    ): Promise<ReorderResult[]> {
        const client = getWorkClient(this.pageContext);
        const tfsPageData = this.pageContext.getService<ITfsPageService>("ITfsPageService").getData();

        //for agile backlog we need to use special reorder method
        if (iterationId) {
            const parentId = parentItem && parentItem.id > 0 ? parentItem.id : 0;

            return await client.reorderIterationWorkItems(
                {
                    ids: workItemIds,
                    iterationPath: iterationPath || "",
                    nextId: nextItemId || 0,
                    previousId: previousItemId || 0,
                    parentId: parentId
                },
                {
                    projectId: tfsPageData?.project?.id ?? "",
                    teamId: this.commonBacklogContentService.getCurrentTeam().id
                } as TeamContext,
                iterationId || ""
            );
        } else {
            return await client.reorderBacklogWorkItems(
                {
                    ids: workItemIds,
                    iterationPath: iterationPath || "",
                    nextId: nextItemId || 0,
                    previousId: previousItemId || 0,
                    parentId: parentItem?.id || 0
                },
                {
                    projectId: tfsPageData?.project?.id ?? "",
                    teamId: this.commonBacklogContentService.getCurrentTeam().id
                } as TeamContext
            );
        }
    }

    private async reorderTreeWorkItems(
        rows: ITreeItem<IBacklogTreeRow>[],
        direction: TopBottomSelection | undefined,
        workItemId: number,
        parentItem?: ITreeItem<IBacklogTreeRow>,
        backlogTree?: IBacklogTree,
        item?: ITreeItem<IBacklogTreeRow>
    ) {
        let reorderResult: ReorderResult[] | undefined = undefined;
        if (direction === AddWorkItemLocations.Top) {
            let firstWorkItemId: number = rows[0]?.data?.id;
            if (!firstWorkItemId) {
                return reorderResult;
            }

            if (!parentItem && firstWorkItemId < 0) {
                const backlogLevels = await this.getBacklogLevels();
                firstWorkItemId = this.getNextFirstSuitableItemId(rows, backlogLevels, 0);
            } else if (parentItem) {
                firstWorkItemId = parentItem.data.children[0]?.id || 0;
            }
            reorderResult = await this.reorderBacklogWorkItems([workItemId], null, firstWorkItemId);
        } else if (direction === AddWorkItemLocations.Selection) {
            if (parentItem && backlogTree) {
                if (!item) {
                    const prevItemId = 0;
                    const nextItemId = parentItem.data.children[0]?.id || 0;

                    if (nextItemId === 0) {
                        return reorderResult;
                    }

                    return await this.reorderBacklogWorkItems([workItemId], prevItemId, nextItemId, parentItem.data);
                }

                const itemId = item.data.id;
                const index = parentItem.data.children.findIndex(item => item.id === itemId);
                if (index >= 0) {
                    const prevItemId = parentItem.data.children[index].id > 0 ? parentItem.data.children[index].id : null;

                    const nextItem = parentItem.data.children[index + 1];
                    const nextItemId = nextItem && nextItem.id > 0 ? nextItem.id : null;
                    reorderResult = await this.reorderBacklogWorkItems([workItemId], prevItemId, nextItemId, parentItem.data);
                }
            } else {
                const selectedItemIndex = rows.findIndex(row => row.data.id === item?.data.id);
                if (selectedItemIndex >= 0) {
                    const prevItem = rows[selectedItemIndex];
                    const prevItemId = prevItem.data.id > 0 ? prevItem.data.id : null;

                    const nextItem = rows[selectedItemIndex + 1];
                    const nextItemId = nextItem?.data.id > 0 ? nextItem.data.id : null;

                    reorderResult = await this.reorderBacklogWorkItems([workItemId], prevItemId, nextItemId);
                }
            }
        }
        return reorderResult;
    }

    private getCurrentBacklogLevel(backlogLevels: BacklogLevelConfiguration[]): BacklogLevelConfiguration | undefined {
        const backlogLevelId = this.commonBacklogContentService.getBacklogLevelId();
        return backlogLevels.find(level => level.id === backlogLevelId || level.name === backlogLevelId);
    }

    private getWorkItemBacklogLevel(
        item: ITreeItemEx<IBacklogTreeRow> | IBacklogTreeRow,
        backlogLevels: BacklogLevelConfiguration[]
    ): BacklogLevelConfiguration | undefined {
        let itemTypeName: string | undefined;
        let backlogLevelName: string | undefined;
        if ("underlyingItem" in item) {
            itemTypeName = item.underlyingItem.data.value.workItemTypeColorAndIcon?.workItemTypeName;
            backlogLevelName = item.underlyingItem.data.value.backlogLevel;
        } else {
            itemTypeName = item.value.workItemTypeColorAndIcon?.workItemTypeName;
            backlogLevelName = item.value.backlogLevel;
        }

        if (!itemTypeName && !backlogLevelName) {
            return undefined;
        }
        return backlogLevels.find(level => {
            if (backlogLevelName) {
                return level.name === backlogLevelName;
            }
            return level.workItemTypes.some(type => type.name === itemTypeName);
        });
    }

    private getNextFirstSuitableItemId(rows: ITreeItem<IBacklogTreeRow>[], backlogLevels: BacklogLevelConfiguration[], startIndex: number): number {
        const currentBacklogLevel = this.getCurrentBacklogLevel(backlogLevels);
        if (currentBacklogLevel) {
            for (let i = startIndex + 1; i < rows.length; i++) {
                if (currentBacklogLevel.workItemTypes.some(level => rows[i].data.value.workItemTypeColorAndIcon?.workItemTypeName === level.name)) {
                    return rows[i].data.id;
                }
            }
        }
        return rows[startIndex].data.id;
    }

    private getFirstAppropriateParentItem = (
        backlogLevels: BacklogLevelConfiguration[],
        currentBacklogLevel: BacklogLevelConfiguration,
        item?: ITreeItemEx<IBacklogTreeRow>
    ): ITreeItemEx<IBacklogTreeRow> | undefined => {
        if (!item || !item.parentItem || this.getWorkItemBacklogLevel(item, backlogLevels)?.rank === currentBacklogLevel.rank) {
            return item;
        }
        return this.getFirstAppropriateParentItem(backlogLevels, currentBacklogLevel, item.parentItem);
    };

    private doFindAppropriateChild(
        backlogLevels: BacklogLevelConfiguration[],
        currentBacklogLevel: BacklogLevelConfiguration,
        item: ITreeItem<IBacklogTreeRow>
    ): ITreeItem<IBacklogTreeRow> | undefined {
        const itemLevel = this.getWorkItemBacklogLevel(item.data, backlogLevels);
        if (!itemLevel) {
            return undefined;
        }
        if ((item.data.id > 0 && itemLevel.rank - currentBacklogLevel.rank === 1) || itemLevel.rank <= currentBacklogLevel.rank) {
            // return if it's a real WI with +1 rank or folder with the same rank
            // we want to find an item 1 level higher than current backlog level
            return item;
        }

        if (item.childItems?.[0]) {
            return this.doFindAppropriateChild(backlogLevels, currentBacklogLevel, item.childItems[0]);
        }
        return undefined;
    }

    private findInsertPlaceAmongNextNeighbours(
        rows: ITreeItem<IBacklogTreeRow>[],
        backlogLevels: BacklogLevelConfiguration[],
        currentBacklogLevel: BacklogLevelConfiguration,
        startItem: ITreeItem<IBacklogTreeRow>
    ): ITreeItem<IBacklogTreeRow> | undefined {
        const startItemIndex = rows.findIndex(row => row.data.id === startItem.data.id);
        for (let i = startItemIndex + 1; i < rows.length; i++) {
            const foundChild = this.doFindAppropriateChild(backlogLevels, currentBacklogLevel, rows[i]);
            if (foundChild) {
                return foundChild;
            }
        }
        return undefined;
    }

    private findRoot = (selectedItem: ITreeItemEx<IBacklogTreeRow>): ITreeItemEx<IBacklogTreeRow> => {
        if (!selectedItem.parentItem) {
            return selectedItem;
        }
        return this.findRoot(selectedItem.parentItem);
    };

    private findAppropriateChild(
        rows: ITreeItem<IBacklogTreeRow>[] | undefined,
        backlogLevels: BacklogLevelConfiguration[],
        currentBacklogLevel: BacklogLevelConfiguration,
        selectedItem: ITreeItemEx<IBacklogTreeRow>
    ): ITreeItem<IBacklogTreeRow> | undefined {
        if (rows) {
            // try to find appropriate place to insert among direct children of selected item
            const foundForSelectedItem = this.doFindAppropriateChild(backlogLevels, currentBacklogLevel, selectedItem.underlyingItem);
            if (foundForSelectedItem) {
                return foundForSelectedItem;
            }

            // try to find appropriate place to insert among neighbours of selected item of the same level
            if (selectedItem.parentItem?.underlyingItem.childItems) {
                const foundForNeighbours = this.findInsertPlaceAmongNextNeighbours(
                    selectedItem.parentItem.underlyingItem.childItems,
                    backlogLevels,
                    currentBacklogLevel,
                    selectedItem.underlyingItem
                );
                if (foundForNeighbours) {
                    return foundForNeighbours;
                }
            }

            // search appropriate items from the root level
            return this.findInsertPlaceAmongNextNeighbours(rows, backlogLevels, currentBacklogLevel, this.findRoot(selectedItem).underlyingItem);
        }
    }

    private saveWorkItems = async (workItems: IWorkItem[], skipUnchangedItems?: boolean): Promise<boolean> => {
        const savingService = this.pageContext.getService<IWorkItemSavingService>("IWorkItemSavingService");
        try {
            await savingService.saveWorkItemsBatchAsync(workItems);
            this.commonBacklogContentService.clearErrorMessage();
        } catch (error) {
            // this.commonBacklogContentService.refreshTree();
            this.updateIsValid(
                workItems.map(wi => ({ workItem: wi, isValid: false })),
                skipUnchangedItems
            );
            this.commonBacklogContentService.showBulkSaveErrorMessage(error);
            return false;
        }

        this.updateIsValid(
            workItems.map(wi => ({ workItem: wi, isValid: true })),
            skipUnchangedItems
        );
        this.filterManager.dataUpdated();
        return true;
    };

    private updateFilter() {
        this.filterManager.dataUpdated();
        const filter = this.commonBacklogContentService.getFilter();
        filter.setState(filter.getState());
    }

    private updateBacklogTreeRowsAdd(newItem: ITreeItem<IBacklogTreeRow>, parentItem?: ITreeItem<IBacklogTreeRow>) {
        const backlogTree = this.commonBacklogContentService.getBacklogTree().value!;
        backlogTree.allRows[newItem.data.id] = newItem.data;
        this.commonBacklogContentService.addRowToIdsMap(newItem.data);

        // item provider has proper list of root items with proper children
        backlogTree.rows = backlogTree.itemProvider.roots;

        if (parentItem) {
            backlogTree.allRows[parentItem.data.id] = parentItem.data;
        }
    }

    private updateIsValid(update: { workItem: IWorkItem; isValid: boolean }[], skipUnchangedItems?: boolean) {
        const backlogTree = this.commonBacklogContentService.getBacklogTree();
        const updatesToUse: { workItemId: number; workItem?: IWorkItem; forceRefresh?: boolean }[] = [];

        if (backlogTree.value) {
            for (const el of update) {
                const itemProvider = backlogTree.value.itemProvider.value;
                const item = itemProvider.find(elem => elem.underlyingItem.data.id === el.workItem.id)?.underlyingItem.data.value;

                if (item) {
                    if (skipUnchangedItems && item.isValid === el.isValid) {
                        continue;
                    }
                    item.isValid = el.isValid;
                }

                updatesToUse.push({ workItemId: el.workItem.id, workItem: el.workItem });
            }
        }

        if (updatesToUse.length > 0) {
            this.commonBacklogContentService.updateWorkItemRows(updatesToUse);
        }
    }

    private setReorderedWorkItemIds(ids: number[], wereReordered: boolean) {
        ids.forEach(id => {
            this.reorderedWorkItemIds[id] = wereReordered;
        });
    }

    public updateWorkItemsByIdIfWereReordered = async (ids: number[]) => {
        const workItemIdsToUpdate = ids.filter(id => this.reorderedWorkItemIds[id]);
        if (!workItemIdsToUpdate.length) {
            return;
        }

        const workItems = await this.getWorkItemFromModelService(workItemIdsToUpdate, true);
        await this.saveWorkItems(workItems);
        this.setReorderedWorkItemIds(workItemIdsToUpdate, false);
    };
}

Services.add("IBacklogWorkItemService", { serviceFactory: BacklogWorkItemService });
